<template>
  <div id="page-user-list">

    <!-- BARRE D'ACTION GROUPÉE -->
    <div class="vx-card p-4 mb-4">
      <div class="flex flex-wrap items-center justify-between">
        <div class="flex flex-wrap items-center">
          <vs-button
            v-if="!readOnlyRole"
            color="primary"
            icon-pack="feather"
            icon="icon-send"
            :disabled="!selectedRows.length || sending"
            @click="confirmSendSelected">
            Send {{ selectedRows.length }} selected to SPV
          </vs-button>
          <span v-if="selectedRows.length" class="ml-4 text-sm">
            {{ formatMillier(selectedTotalTtc.toFixed(2)) }} RON TTC
          </span>
        </div>
        <div class="flex flex-wrap items-center">
          <!-- Trois vues sur la même liste (centralisateur demandé par la comptable,
               2026-10-29) : ce qui reste à envoyer, ce que l'ANAF a validé, tout. -->
          <vs-button v-for="v in views" :key="v.key" size="small" class="mr-2"
            :type="view === v.key ? 'filled' : 'border'" @click="view = v.key">
            {{ v.label }} ({{ countOf(v.key) }})
          </vs-button>
          <span class="text-sm text-grey ml-2">
            {{ sendableCount }} of {{ visibleRows.length }} invoices can be sent.
            Locked rows carry a ⚠ next to their number — hover it for the reason.
          </span>
        </div>
      </div>
      <!-- Robot « messages ANAF » (migration 103, api/app/services/efactura/anafmessages.js).
           L'ANAF peut valider une facture (« ok ») sans la remettre au client : BT-47,
           client sans CUI, traitée comme B2C (RO2026_01525, 06/10/2026). Seul le
           message ANAF le dit ; le robot le lit toutes les heures et pose l'alerte. -->
      <div class="flex flex-wrap items-center justify-between mt-3 pt-3" style="border-top:1px solid #e3e7e4">
        <span class="text-sm">
          <template v-if="watch && watch.last_ok_at">ANAF messages checked {{ fmtWhen(watch.last_ok_at) }} (every hour).</template>
          <template v-else-if="watch">ANAF messages not checked yet (every hour, from the next deploy).</template>
          <template v-else>ANAF messages: status not loaded.</template>
          <template v-if="watch">
            <b :class="watch.alerts_total ? 'text-danger' : ''">{{ watch.alerts_total ? watch.alerts_total + ' alert' + (watch.alerts_total > 1 ? 's' : '') : 'No alert' }}</b><template v-if="watch.alerts_without_storno">, {{ watch.alerts_without_storno }} still without storno</template><template v-if="watch.b2c_total">; {{ watch.b2c_total }} B2C (private persons, nothing due)</template>.
          </template>
          <span v-if="watch && watch.last_run && watch.last_run.ok === false" class="text-danger ml-1">Last check failed: {{ watch.last_run.error }}</span>
        </span>
        <vs-button v-if="!readOnlyRole" size="small" type="border" icon-pack="feather" icon="icon-refresh-cw"
          :disabled="checking" @click="runWatch">
          {{ checking ? 'Checking ANAF…' : 'Check ANAF messages now' }}
        </vs-button>
      </div>
      <p class="text-xs text-grey mt-2 mb-0">
        The robot reads the ANAF messages every hour. “Not delivered (BT-47)” = the ANAF validated the invoice (“ok”,
        index issued) but could not deliver it: it left without a customer identifier although the customer card carries
        a CUI, VAT number or J number today. Issue a storno and re-issue the invoice. A card with no CUI, VAT number or
        J number is a private person: the invoice is a normal B2C one (“B2C · private person”), nothing is due — unless a
        CUI is added to the card, which turns it red at the next check. The COMPANY/PERSON switch of the card is not
        used. The alert stays as history; the status shows “storno …” once the credit note exists.
      </p>
    </div>

    <div class="vx-card p-6 mb-8">
      <div class="flex flex-wrap items-center">
        <!-- ITEMS PER PAGE -->
        <div class="flex-grow">
          <vs-dropdown vs-trigger-click class="cursor-pointer">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium">
              <span class="mr-2">{{ currentPage * paginationPageSize - (paginationPageSize - 1) }} - {{
                operationsData.length - currentPage * paginationPageSize > 0 ? currentPage * paginationPageSize :
                operationsData.length }} of {{ operationsData.length }}</span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(10)">
                <span>10</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(20)">
                <span>20</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(25)">
                <span>25</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="gridApi.paginationSetPageSize(30)">
                <span>30</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
        <!-- TABLE ACTION COL-2: SEARCH & EXPORT AS CSV -->
        <vs-input class="sm:mr-4 mr-0 sm:w-auto w-full sm:order-normal order-3 sm:mt-0 mt-4" v-model="searchQuery"
          @input="updateSearchQuery" placeholder="Search..." />
        <vs-button class="mb-4 md:mb-0 mr-2" color="success" icon-pack="feather" icon="icon-download" @click="exportExcel">Export Excel</vs-button>
        <vs-button class="mb-4 md:mb-0" @click="gridApi.exportDataAsCsv()">Export as CSV</vs-button>
      </div>
      <ag-grid-vue ref="agGridTable" :components="components" :gridOptions="gridOptions"
        class="ag-theme-material w-100 my-4 ag-grid-table" :columnDefs="columnDefs" :defaultColDef="defaultColDef"
        :rowData="visibleRows" rowSelection="multiple" colResizeDefault="shift" :animateRows="true"
        :floatingFilter="true" :pagination="true" :paginationPageSize="paginationPageSize" :suppressPaginationPanel="true"
        :enableRtl="$vs.rtl">
      </ag-grid-vue>

      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />

    </div>

    <!-- PROGRESSION DE L'ENVOI GROUPÉ -->
    <vs-popup :title="popupTitle" :active.sync="progressPopup" :button-close-hidden="true">
      <div class="mb-4">
        <vs-progress :percent="progressPercent" color="primary">primary</vs-progress>
        <p class="mt-2 text-sm">{{ done }} / {{ jobs.length }} processed — {{ okCount }} sent, {{ koCount }} failed</p>
      </div>

      <div class="efactura-joblist">
        <div v-for="job in jobs" :key="job.id" class="efactura-job">
          <div class="flex items-center justify-between">
            <span class="font-medium">{{ job.invoice_no }}</span>
            <vs-chip :color="statusColor(job.status)">{{ statusLabel(job.status) }}</vs-chip>
          </div>
          <p class="text-sm">{{ job.company_name }}</p>
          <p v-if="job.error" class="text-sm efactura-job-error">{{ job.error }}</p>
        </div>
      </div>

      <div class="mt-4 flex items-center justify-between">
        <!-- On n'interrompt jamais un envoi en cours : l'ANAF a peut-être déjà
             reçu le document. On s'arrête proprement APRÈS la facture courante. -->
        <vs-button v-if="sending" color="warning" type="border" @click="stopRequested = true"
          :disabled="stopRequested">
          {{ stopRequested ? 'Stopping after current invoice…' : 'Stop after current invoice' }}
        </vs-button>
        <vs-button v-else color="primary" @click="closeProgress">Close</vs-button>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import axios from 'axios';

import { AgGridVue } from 'ag-grid-vue'
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss'
import vSelect from 'vue-select'

// Store Module
import moduleBankList from '@/store/bank/moduleDataList.js'

// Cell Renderer
import CellRendererSpvStatus from './cell-renderer/CellRendererSpvStatus.vue'
import CellRendererProducts from './cell-renderer/CellRendererProducts.vue'
import CellRendererInvoice from './cell-renderer/CellRendererInvoice.vue'
import CellRendererReceiver from './cell-renderer/CellRendererReceiver.vue'
import moment from 'moment'
import XLSX from 'xlsx-js-style'
import { isAccountantOnly } from '@/helpers/roleAccess.js'

import efacturaError from '@/mixins/efacturaError.js'
import { loadEfacturaContext } from '@/helpers/efacturaDocument.js'
import { buildEfacturaXml } from '@/helpers/efacturaUbl.js'

export default {
  mixins: [efacturaError],
  components: {
    AgGridVue,
    vSelect,
    // Cell Renderer
    CellRendererSpvStatus,
    CellRendererProducts,
    CellRendererInvoice,
    CellRendererReceiver
  },
  data() {
    return {
      efacturaList: [],
      currentValue: null,
      searchQuery: '',
      // Envoi groupé — masqué pour la comptable (rôle en lecture seule : le
      // serveur refuserait de toute façon le POST, 403 read_only_role).
      readOnlyRole: isAccountantOnly(),
      view: 'tosend',
      views: [
        { key: 'tosend', label: 'To send' },
        { key: 'validated', label: 'Validated by ANAF' },
        { key: 'alerts', label: 'ANAF alerts' },
        { key: 'all', label: 'All' }
      ],
      // Robot « messages ANAF » : dernier passage, alertes ; « Check now ».
      watch: null,
      checking: false,
      selectedRows: [],
      sending: false,
      stopRequested: false,
      progressPopup: false,
      jobs: [],
      // AgGrid
      gridApi: null,
      gridOptions: {
        // Le client tient sur deux lignes (raison sociale + CUI) : 48 px les
        // écraseraient.
        rowHeight: 56,
        // Seules les factures que l'API déclare envoyables sont cochables : la
        // règle d'éligibilité (adresse de facturation, statut SPV, lignes) est
        // calculée côté serveur avec la MÊME fonction que le garde-fou d'envoi.
        isRowSelectable: (node) => !!(node.data && node.data.can_send),
        // On lit l'API portée par l'événement : `this.gridApi` n'est affecté
        // qu'au `mounted`, or la grille peut émettre une sélection avant.
        onSelectionChanged: (event) => {
          const api = (event && event.api) || this.gridApi
          this.selectedRows = api ? api.getSelectedRows() : []
        }
      },
      suppressSizeToFit: true,
      defaultColDef: {
        sortable: false,
        resizable: true,
        suppressMenu: true
      },
      columnDefs: [
        {
          // La case vit dans SA colonne : quand ag-grid n'en rend pas (ligne
          // non cochable), le numéro de facture ne se décale plus vers la
          // gauche — toute la colonne reste alignée.
          headerName: '',
          width: 46,
          suppressSizeToFit: true,
          suppressMenu: true,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true
        },
        {
          headerName: 'INVOICE NO',
          valueGetter: function (params) {
            return params.data.invoice_no
          },
          filter: true,
          width: 240,
          cellRendererFramework: 'CellRendererInvoice'
        },
        {
          headerName: 'DATE',
          // `date::date` arrive en ISO (2026-09-05) : illisible d'un coup d'œil
          // et trop long pour la colonne, d'où le format court.
          valueGetter: function (params) {
            return params.data.date ? moment(String(params.data.date)).format('DD/MM/YYYY') : ''
          },
          filter: true,
          width: 120
        },
        {
          // Raison sociale ET CUI dans la même cellule : deux colonnes pour la
          // même entité coûtaient 150 px de large pour rien. Le filtre porte sur
          // les deux, et l'export CSV les garde.
          headerName: 'RECEIVER',
          valueGetter: function (params) {
            const cui = params.data.cui && params.data.cui !== '-' ? params.data.cui.replace('RO', '') : 'FIZIC'
            return (params.data.company_name || '') + ' — ' + cui
          },
          filter: true,
          width: 250,
          cellRendererFramework: 'CellRendererReceiver'
        },
        {
          // L'identifiant que l'XML porte vraiment : numéro de TVA, sinon 13
          // zéros (FIZIC). Une société sans numéro de TVA part en personne
          // physique et n'atteint aucun SPV (RO2026_01525, 06/10/2026) : en
          // orange, pour qu'on complète la fiche AVANT d'envoyer.
          headerName: 'IDENTIFIER',
          valueGetter: function (params) {
            const d = params.data
            if (d.identifier_kind === 'VAT') return 'VAT ' + d.identifier
            if (d.identifier_kind === 'CUI') return 'CUI ' + d.identifier + ' — sent as FIZIC'
            return d.company_without_vat ? 'FIZIC — company without VAT no.' : 'FIZIC'
          },
          cellClassRules: { 'efactura-identifier-warn': (params) => !!params.data.company_without_vat },
          filter: true,
          sortable: true,
          width: 210
        },
        {
          headerName: 'UPLOAD ID',
          valueGetter: (params) => params.data.efactura_idincarcare || '',
          filter: true,
          sortable: true,
          width: 130
        },
        {
          headerName: 'DOWNLOAD ID',
          valueGetter: (params) => params.data.efactura_iddescarcare || '',
          filter: true,
          sortable: true,
          width: 140
        },
        {
          // Depuis l'archive efactura_log (migration 101) : vide pour les
          // envois antérieurs à sa mise en place.
          headerName: 'SENT ON',
          valueGetter: (params) => params.data.efactura_sent_at ? moment(String(params.data.efactura_sent_at)).format('DD/MM/YYYY HH:mm') : '',
          sortable: true,
          width: 140
        },
        {
          headerName: 'IN SAGA',
          valueGetter: (params) => this.sagaLabel(params.data),
          filter: true,
          sortable: true,
          width: 120
        },
        {
          headerName: 'VAT %',
          valueGetter: function (params) {
            // Plusieurs taux sur un même document -> « 0 / 21 ». Afficher un
            // seul taux donnerait un TTC faux à qui le recalcule.
            return params.data.vat_rates || '—'
          },
          filter: true,
          headerClass: 'ag-right-aligned-header',
          cellClass: 'text-right',
          width: 105
        },
        {
          headerName: 'TOTAL RON',
          valueGetter: (params) => this.formatMillier(Number(params.data.ttc_ron || 0).toFixed(2)),
          headerClass: 'ag-right-aligned-header',
          cellClass: 'text-right font-semibold',
          width: 150
        },
        {
          // Prix de vente moyen au m3, MEME definition que `docPricePerM3` du
          // mixin documentTotals (HT EUR / volume) : c'est le chiffre des ecrans
          // de document, pas un second calcul. Seule colonne en euros de la
          // liste — le prix du bois se juge en EUR/m3, la e-factura se declare
          // en RON.
          headerName: '\u20AC/m\u00B3 HT',
          valueGetter: (params) => {
            const p = params.data.price_eur_m3
            // Vide, pas « 0,00 » : un document sans volume (transport seul,
            // prestation) n'a pas de prix au m3, et un 0 se lirait comme un prix.
            return p === null || p === undefined ? '' : this.formatMillier(Number(p).toFixed(2))
          },
          headerClass: 'ag-right-aligned-header',
          cellClass: 'text-right',
          width: 115
        },
        {
          headerName: 'PRODUCTS',
          valueGetter: function (params) {
            return params.data.products_summary
          },
          filter: true,
          width: 300,
          cellRendererFramework: 'CellRendererProducts'
        },
        {
          // Le libellé reste dans le valueGetter : c'est lui qui sert au filtre
          // et aux exports. Les identifiants ANAF ont leurs colonnes.
          headerName: 'STATUS',
          field: 'STATUS',
          valueGetter: (params) => this.anafLabel(params.data),
          cellRendererFramework: 'CellRendererSpvStatus',
          filter: true,
          sortable: true,
          width: 180
        }
      ],

      // Cell Renderer Components
      components: {
        CellRendererSpvStatus,
        CellRendererProducts,
        CellRendererInvoice,
        CellRendererReceiver
      }
    }
  },
  computed: {
    operationsData() {
      return this.$store.state.BankList.efacturastobesent
    },
    visibleRows() {
      return this.operationsData.filter(r => this.inView(r, this.view))
    },
    sendableCount() {
      return this.visibleRows.filter(r => r.can_send).length
    },
    selectedTotalTtc() {
      return this.selectedRows.reduce((sum, r) => sum + (Number(r.ttc_ron) || 0), 0)
    },
    done() {
      return this.jobs.filter(j => j.status === 'ok' || j.status === 'error' || j.status === 'skipped').length
    },
    okCount() {
      return this.jobs.filter(j => j.status === 'ok').length
    },
    koCount() {
      return this.jobs.filter(j => j.status === 'error').length
    },
    progressPercent() {
      if (!this.jobs.length) return 0
      return Math.round((this.done / this.jobs.length) * 100)
    },
    popupTitle() {
      return this.sending ? 'Sending to ANAF…' : 'Batch finished'
    },
    paginationPageSize() {
      if (this.gridApi) return this.gridApi.paginationGetPageSize()
      else return 100
    },
    totalPages() {
      if (this.gridApi) return this.gridApi.paginationGetTotalPages()
      else return 0
    },
    currentPage: {
      get() {
        if (this.gridApi) return this.gridApi.paginationGetCurrentPage() + 1
        else return 1
      },
      set(val) {
        this.gridApi.paginationGoToPage(val - 1)
      }
    }
  },
  methods: {
    inView(row, view) {
      if (view === 'alerts') return row.efactura_alert === 'NOT_DELIVERED' || row.efactura_alert === 'ANAF_ERROR'
      if (view === 'validated') return row.efactura_stare === 'ok'
      if (view === 'tosend') return row.efactura_stare !== 'ok'
      return true
    },
    countOf(view) {
      return this.operationsData.filter(r => this.inView(r, view)).length
    },
    // « ok » = validée et signée par l'ANAF, pas « reçue par le client » : une
    // facture partie en 13 zéros est « ok » sans atteindre aucun SPV.
    // L'alerte du robot « messages ANAF » prime : « ok » chez l'ANAF, mais jamais
    // remise au client (BT-47). Même libellé que CellRendererSpvStatus.
    anafLabel(row) {
      const r = row || {}
      if (r.efactura_alert === 'NOT_DELIVERED') return 'NOT DELIVERED (BT-47)'
      if (r.efactura_alert === 'ANAF_ERROR') return 'ANAF ERROR'
      if (r.efactura_alert === 'B2C_PERSON') return 'B2C · PRIVATE PERSON'
      if (r.efactura_stare === 'ok') return 'VALIDATED BY ANAF'
      if (r.efactura_stare === 'nok') return 'REJECTED BY ANAF'
      if (r.efactura_stare === 'in prelucrare') return 'ANAF PROCESSING'
      return 'NOT SENT'
    },
    fmtWhen(d) {
      return d ? moment(String(d)).format('DD/MM/YYYY HH:mm') : ''
    },
    loadWatch() {
      axios.get('/anaf/efactura/watch')
        .then(r => { this.watch = r.data })
        .catch(() => { this.watch = null })
    },
    /** Un passage du robot à la demande ; la liste est rechargée s'il a posé des alertes. */
    runWatch() {
      this.checking = true
      axios.post('/anaf/efactura/watch/run', {})
        .then(r => {
          const d = r.data || {}
          this.$vs.notify({
            color: d.alerts_new ? 'danger' : 'success',
            title: 'ANAF messages checked',
            text: d.skipped
              ? 'A check is already running.'
              : `${d.messages_read} message(s) read over ${d.days} day(s), ${d.messages_new} new, ${d.alerts_new} new alert(s).`,
            time: 8000
          })
          this.loadWatch()
          if (d.alerts_new) this.$store.dispatch('BankList/uploadInvoiceList', { scope: 'all' })
        })
        .catch(err => {
          this.$vs.notify({ color: 'danger', title: 'ANAF messages check failed', text: this.efacturaErrorMessage(err), time: 10000 })
        })
        .then(() => { this.checking = false })
    },
    sagaLabel(row) {
      if (row.saga_sent_at) return moment(String(row.saga_sent_at)).format('DD/MM/YYYY')
      return row.issyncsaga ? 'yes' : ''
    },
    // ---- Export Excel : les lignes telles que la grille les montre (vue, recherche, tri) ----
    xlsNum(v) {
      if (v == null || v === '') return null
      if (typeof v === 'number') return v
      const n = parseFloat(String(v).replace(/\s/g, '').replace(',', '.'))
      return Number.isNaN(n) ? v : n
    },
    // columns : [{ h: en-tête, v: ligne => valeur, w?: largeur }]
    xlsSheet(wb, name, columns, rows) {
      const aoa = [columns.map(c => c.h)]
      rows.forEach(r => aoa.push(columns.map(c => { const v = c.v(r); return v === undefined ? null : v })))
      const ws = XLSX.utils.aoa_to_sheet(aoa)
      columns.forEach((c, ci) => {
        const head = ws[XLSX.utils.encode_cell({ r: 0, c: ci })]
        if (head) head.s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '2C3E50' } } }
      })
      ws['!cols'] = columns.map((c, ci) => ({
        wch: c.w || Math.min(60, Math.max(c.h.length + 2, ...aoa.slice(1, 200).map(a => String(a[ci] == null ? '' : a[ci]).length + 1)))
      }))
      ws['!freeze'] = { xSplit: 0, ySplit: 1 }
      if (rows.length) ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: rows.length, c: columns.length - 1 } }) }
      XLSX.utils.book_append_sheet(wb, ws, name)
    },
    exportExcel() {
      const rows = []
      if (this.gridApi) this.gridApi.forEachNodeAfterFilterAndSort(n => { if (n.data) rows.push(n.data) })
      const wb = XLSX.utils.book_new()
      const fmt = (d, withHour) => d ? moment(String(d)).format(withHour ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY') : null
      this.xlsSheet(wb, 'e-Factura', [
        { h: 'Invoice no', v: r => r.invoice_no, w: 16 },
        { h: 'Date', v: r => fmt(r.date) },
        { h: 'Customer', v: r => r.company_name, w: 40 },
        { h: 'Identifier used', v: r => r.identifier_kind === 'FIZIC' ? 'FIZIC (0000000000000)' : r.identifier_kind },
        { h: 'VAT number', v: r => r.vat_number || null },
        { h: 'CUI on record', v: r => r.cui_record || null },
        { h: 'Company without VAT no.', v: r => r.company_without_vat ? 'YES' : null },
        { h: 'Upload ID', v: r => r.efactura_idincarcare || null },
        { h: 'Download ID', v: r => r.efactura_iddescarcare || null },
        { h: 'ANAF state', v: r => this.anafLabel(r) },
        { h: 'ANAF message', v: r => r.efactura_alert_detail || null, w: 60 },
        { h: 'ANAF message date', v: r => fmt(r.efactura_alert_at, true) },
        { h: 'Storno', v: r => r.storno_no || null },
        { h: 'Sent on', v: r => fmt(r.efactura_sent_at, true) },
        { h: 'In SAGA', v: r => this.sagaLabel(r) || null },
        { h: 'Total RON incl. VAT', v: r => this.xlsNum(r.ttc_ron) },
        { h: 'VAT %', v: r => r.vat_rates || null },
        { h: 'Type', v: r => r.iscreditnote ? 'STORNO' : r.is_firewood ? 'FIREWOOD' : 'INVOICE' },
        { h: 'Blocked reason', v: r => r.blocked_reason || null, w: 50 }
      ], rows)
      const viewLabel = (this.views.find(v => v.key === this.view) || {}).label || this.view
      this.xlsSheet(wb, 'Filters', [
        { h: 'Filter', v: x => x[0], w: 24 },
        { h: 'Value', v: x => x[1], w: 60 }
      ], [
        ['View', viewLabel],
        ['Table search', this.searchQuery || 'none'],
        ['Rows exported', rows.length],
        ['Rows in view', this.visibleRows.length],
        ['Exported at', moment().format('YYYY-MM-DD HH:mm')],
        ['Note', '"VALIDATED BY ANAF" = accepted and signed by ANAF. A FIZIC identifier means the invoice reached no company SPV.']
      ])
      XLSX.writeFile(wb, 'efactura-centralizator-' + moment().format('YYYY-MM-DD') + '.xlsx')
    },
    formatMillier(nombre, sep) {
      sep = sep || " "
      let n = String(nombre).replace(".", ",")
      const reg = /(\d+)(\d{3})/
      // Le séparateur de milliers ne s'applique qu'à la partie entière.
      let [entier, dec] = n.split(",")
      while (reg.test(entier)) {
        entier = entier.replace(reg, "$1" + sep + "$2")
      }
      return dec ? entier + "," + dec : entier
    },
    confirmSendSelected() {
      const n = this.selectedRows.length
      if (!n) return
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: `Send ${n} e-Factura to ANAF?`,
        text:
          `${n} invoice(s), ${this.formatMillier(this.selectedTotalTtc.toFixed(2))} RON incl. VAT.\n` +
          'Each invoice is validated by ANAF first, then sent. Invoices are processed one by one.',
        acceptText: 'Send',
        accept: this.sendSelected,
      })
    },
    /**
     * Envoi groupé, STRICTEMENT séquentiel : le token ANAF est partagé et le
     * rafraîchissement de statut interroge l'ANAF facture par facture. Une
     * facture en échec n'interrompt pas le lot — elle est reportée à la fin.
     */
    async sendSelected() {
      this.jobs = this.selectedRows.map(r => ({
        id: r.id,
        invoice_no: r.invoice_no,
        company_name: r.company_name,
        status: 'pending',
        error: null,
      }))
      this.sending = true
      this.stopRequested = false
      this.progressPopup = true

      for (const job of this.jobs) {
        if (this.stopRequested) {
          job.status = 'skipped'
          continue
        }
        try {
          job.status = 'building'
          // Contexte + XML : exactement ce que produit la fiche de cette facture.
          const ctx = await loadEfacturaContext(job.id)
          const xmldata = buildEfacturaXml(ctx)

          // Contrôle ANAF (l'ancien « Review PDF »), sans télécharger le PDF.
          job.status = 'validating'
          await this.$store.dispatch('BankList/testXMLEfacturaSilent', { xmldata, idoperation: job.id })

          job.status = 'sending'
          await this.$store.dispatch('BankList/uploadInvoiceXMLEfactura', { xmldata, idoperation: job.id })
          job.status = 'ok'
        } catch (err) {
          job.status = 'error'
          job.error = this.jobErrorMessage(err)
          console.error('e-Factura batch', job.invoice_no, err)
        }
      }

      this.sending = false
      this.$vs.notify({
        color: this.koCount ? 'warning' : 'success',
        title: 'e-Factura batch finished',
        text: `${this.okCount} sent, ${this.koCount} failed.`,
        time: 8000,
      })
    },
    /**
     * Un seul endroit pour traduire une erreur en phrase : refus typé de l'API
     * (mixin efacturaError), refus volontaire du générateur, ou erreur brute.
     */
    jobErrorMessage(err) {
      if (err && err.response) {
        const en = this.efacturaErrorMessage(err)
        const ro = this.efacturaErrorMessageRo(err)
        const details = (err.response.data && err.response.data.details) || ''
        return [en, ro, details].filter(Boolean).join(' — ')
      }
      return (err && err.message) || 'Unknown error.'
    },
    statusLabel(status) {
      return {
        pending: 'waiting',
        building: 'building XML',
        validating: 'ANAF check',
        sending: 'sending',
        ok: 'sent',
        error: 'failed',
        skipped: 'stopped',
      }[status] || status
    },
    statusColor(status) {
      if (status === 'ok') return 'success'
      if (status === 'error') return 'danger'
      if (status === 'pending' || status === 'skipped') return 'warning'
      return 'primary'
    },
    /** La liste n'est rechargée qu'UNE fois, en fin de lot : /invoicetobesent
     *  déclenche un refreshStatus() qui interroge l'ANAF ligne par ligne. */
    closeProgress() {
      this.progressPopup = false
      this.jobs = []
      this.selectedRows = []
      if (this.gridApi) this.gridApi.deselectAll()
      this.$vs.loading()
      this.$store.dispatch('BankList/uploadInvoiceList', { scope: 'all' })
        .then(() => this.$vs.loading.close())
        .catch(() => this.$vs.loading.close())
    },
    formatDateSPV(dateStr) {
      if (dateStr && dateStr.length === 12) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const hour = dateStr.substring(8, 10);
        const minute = dateStr.substring(10, 12);
        const date = moment(`${year}-${month}-${day} ${hour}:${minute}`, 'YYYY-MM-DD HH:mm');
        return date.format('DD/MM/YYYY HH:mm');
      } else {
        return 'Format de date invalide';
      }
    },
    format_dates(value, withhour) {
      if (value) {
        if (withhour)
          return moment(String(value)).format("DD MMM YYYY HH:mm:ss");
        else return moment(String(value)).format("DD MMM YYYY");
      }
    },
    formatDate(dateString) {
      let date = new Date(dateString);
      let day = date.getDate().toString().padStart(2, '0');
      let month = (date.getMonth() + 1).toString().padStart(2, '0'); // Les mois sont indexés à partir de 0
      let year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    setColumnFilter(column, val) {
      const filter = this.gridApi.getFilterInstance(column)
      let modelObj = null

      if (val !== 'all') {
        modelObj = { type: 'equals', filter: val }
      }

      filter.setModel(modelObj)
      this.gridApi.onFilterChanged()
    },
    updateSearchQuery(val) {
      this.gridApi.setQuickFilter(val)
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api

  },
  created() {

    if (!moduleBankList.isRegistered) {
      this.$store.registerModule('BankList', moduleBankList)
      moduleBankList.isRegistered = true
    }
    if (localStorage.getItem("login-roles").toLowerCase().includes("user")) {
      this.$vs.notify({
        title: "Denied",
        text: "Access to the this page is reserved for administrators",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "danger",
        position: "bottom-right",
        time: 8000,
      });
      this.$router.push({ path: "/dashboard/analytics" });
    }

    //this.$vs.loading()


    this.loadWatch()
    try {
      // Tout depuis 2025, validées comprises : les vues filtrent côté client.
      this.$store.dispatch('BankList/uploadInvoiceList', { scope: 'all' })
        .then(response => {
          this.efacturaList = response.data
          this.$vs.loading.close()
        })
        .catch(err => {
          this.$vs.notify({
            title: "Access Denied",
            text: "Please create a SPV TOKEN",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "danger",
            position: "bottom-center",
            time: 8000,
          });
          console.error(err)
          this.$vs.loading.close()

          this.$router.push({ path: "/settings/menu" });

        })

    } catch (error) {
      console.error("Error in component created hook:", error);
    }
  },
}

</script>

<style lang="scss">
#page-user-list {
  // ag-grid 21 ne fournit pas `ag-right-aligned-header` : les en-têtes des
  // colonnes de montants seraient à gauche au-dessus de chiffres alignés à
  // droite. On l'ajoute ici.
  .ag-grid-table .ag-right-aligned-header .ag-header-cell-label {
    justify-content: flex-end;
  }

  .ag-grid-table .efactura-identifier-warn {
    color: #ff9f43;
    font-weight: 600;
  }

  .user-list-filters {
    .vs__actions {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-58%);
    }
  }
}

.efactura-joblist {
  max-height: 45vh;
  overflow-y: auto;
}

.efactura-job {
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.efactura-job-error {
  color: #ea5455;
  white-space: pre-wrap;
}

.context-menu {
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.context-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.context-menu ul li {
  padding: 10px;
  cursor: pointer;
}

.context-menu ul li:hover {
  background-color: #f0f0f0;
}
</style>
